import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Person as PersonIcon,
  AccessTime as AccessTimeIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import { logsApi } from '../services/api';

const Dashboard = () => {
  const [activeUsers, setActiveUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [minutes, setMinutes] = useState(15);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchActiveUsers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await logsApi.getActiveUsers({ minutes });
      setActiveUsers(data.activeUsers || []);
    } catch (err) {
      setError(err.message || 'Failed to fetch active users from main application');
      console.error('Failed to fetch active users:', err);
    } finally {
      setLoading(false);
    }
  }, [minutes]);

  const fetchStats = useCallback(async () => {
    try {
      const data = await logsApi.getStats();
      setStats(data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, []);

  useEffect(() => {
    fetchActiveUsers();
    fetchStats();
  }, [fetchActiveUsers, fetchStats]);

  // Auto refresh every 30 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchActiveUsers();
      fetchStats();
    }, 30000); // 30 seconds

    return () => clearInterval(interval);
  }, [autoRefresh, fetchActiveUsers, fetchStats]);

  const getStatusColor = (activeMinutes) => {
    if (activeMinutes <= 2) return 'success';
    if (activeMinutes <= 5) return 'warning';
    return 'default';
  };

  const getStatusText = (activeMinutes) => {
    if (activeMinutes <= 2) return 'Active';
    if (activeMinutes <= 5) return 'Recently Active';
    return 'Inactive';
  };

  return (
    <Container maxWidth="xl" sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" gutterBottom>
            Dashboard
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Real-time monitoring and statistics
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Activity Window</InputLabel>
            <Select
              value={minutes}
              label="Activity Window"
              onChange={(e) => setMinutes(e.target.value)}
            >
              <MenuItem value={5}>Last 5 minutes</MenuItem>
              <MenuItem value={15}>Last 15 minutes</MenuItem>
              <MenuItem value={30}>Last 30 minutes</MenuItem>
              <MenuItem value={60}>Last 1 hour</MenuItem>
            </Select>
          </FormControl>
          <Tooltip title={autoRefresh ? 'Auto-refresh enabled' : 'Auto-refresh disabled'}>
            <Button
              variant={autoRefresh ? 'contained' : 'outlined'}
              size="small"
              onClick={() => setAutoRefresh(!autoRefresh)}
            >
              Auto-refresh: {autoRefresh ? 'ON' : 'OFF'}
            </Button>
          </Tooltip>
          <IconButton onClick={fetchActiveUsers} disabled={loading}>
            <RefreshIcon />
          </IconButton>
        </Box>
      </Box>

      {/* Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Users
              </Typography>
              <Typography variant="h4">
                {loading ? <CircularProgress size={24} /> : activeUsers.length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Total Logs
              </Typography>
              <Typography variant="h4">
                {stats?.byLevel?.reduce((sum, item) => sum + item.count, 0) || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Error Logs
              </Typography>
              <Typography variant="h4" color="error">
                {stats?.byLevel?.find(s => s._id === 'error')?.count || 0}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography color="text.secondary" gutterBottom>
                Active Sessions
              </Typography>
              <Typography variant="h4">
                {activeUsers.filter(u => u.activeMinutes <= 5).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Active Users Table */}
      <Paper>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Currently Active Users (Last {minutes} minutes)
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Data from main application
          </Typography>
        </Box>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User ID</TableCell>
                <TableCell>Session ID</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell>Active For</TableCell>
                <TableCell>Log Count</TableCell>
                <TableCell>Categories</TableCell>
                <TableCell>Latest Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : activeUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No active users in the last {minutes} minutes
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                activeUsers.map((user, index) => (
                  <TableRow key={`${user.userId}-${user.sessionId}-${index}`} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon fontSize="small" color="primary" />
                        <Typography fontWeight={500}>{user.userId}</Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                        {user.sessionId.substring(0, 8)}...
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusText(user.activeMinutes)}
                        color={getStatusColor(user.activeMinutes)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <AccessTimeIcon fontSize="small" color="action" />
                        <Typography variant="body2">
                          {format(new Date(user.lastSeen), 'HH:mm:ss')}
                        </Typography>
                      </Box>
                      <Typography variant="caption" color="text.secondary">
                        {formatDistanceToNow(new Date(user.lastSeen), { addSuffix: true })}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {user.activeMinutes} min ago
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={user.logCount} size="small" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {user.categories.slice(0, 3).map((cat, idx) => (
                          <Chip key={idx} label={cat} size="small" variant="outlined" />
                        ))}
                        {user.categories.length > 3 && (
                          <Chip label={`+${user.categories.length - 3}`} size="small" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography
                        variant="body2"
                        sx={{
                          maxWidth: 200,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                        title={user.latestMessage}
                      >
                        {user.latestMessage || 'N/A'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Container>
  );
};

export default Dashboard;

